import { zod4Resolver } from "mantine-form-zod-resolver"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { useFindTags } from "~/gen/api"
import { TypesForm } from "~/pages/Admin/components/TypesForm"
import type { BusinessPartnerRepositoryAddressSnapshot } from "~/pages/Admin/components/form.types"
import {
  BusinessPartnerRepositoryPrefillSelection,
  FormConfig,
} from "~/pages/Admin/components/form.types"
import { formSchema as contactFormSchema } from "~/pages/Brand/Suppliers/SuppliersRepository/supplierFormSchema"
import { SupplierFormValues } from "~/pages/Brand/Suppliers/SuppliersRepository/hooks/columns"
import {
  defaultUpdatePermissions,
  UpdatePermissions,
  useFields,
} from "~/pages/Brand/Suppliers/SuppliersRepository/utils/useFormFieldConfig"
import {
  type SupplierMapping,
  toPolicyAwarePayload,
} from "~/pages/SupplierPortal/TraceabilityRequests/components/contactFormPayload"
import { useIsOrganizationFieldLockedForBpRepository } from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useIsOrganizationFieldLockedForBpRepository"
import ProductionSitesInformation from "~/pages/SupplierPortal/ProductionSites/components/ProductionSitesInformation"
import { useAuth } from "~/store/auth-context"

export type ContactFormPanelProps = {
  readonly isActive: boolean
  readonly supplierMapping: SupplierMapping
  readonly handleSupplierMapping: (
    values: SupplierMapping,
    options?: {
      repositoryRecord?: BusinessPartnerRepositoryAddressSnapshot
      duplicateCandidateId?: string
    }
  ) => void
  readonly isUpdate: boolean
  readonly updatePermissions?: UpdatePermissions
  readonly onCancel: () => void
  readonly showAnswerOnBehalfPolicy?: boolean
}

export const ContactFormPanel = ({
  isActive,
  supplierMapping,
  handleSupplierMapping,
  isUpdate = false,
  updatePermissions = defaultUpdatePermissions,
  onCancel,
  showAnswerOnBehalfPolicy = false,
}: ContactFormPanelProps): JSX.Element => {
  const { t } = useTranslation(["organizationSettings"])
  const { user } = useAuth()
  const requesterOrganizationId = user?.organization?.id
  const { data: tagsData } = useFindTags()
  const [repositoryRecord, setRepositoryRecord] =
    useState<BusinessPartnerRepositoryAddressSnapshot | null>(null)
  const [isOwnOrganizationSelected, setIsOwnOrganizationSelected] =
    useState(false)
  const [duplicateCandidateId, setDuplicateCandidateId] = useState<
    string | null
  >(null)
  const [
    repositoryRegistrationIdRemovalBlockedKeys,
    setRepositoryRegistrationIdRemovalBlockedKeys,
  ] = useState<ReadonlySet<string> | null>(null)

  useEffect(() => {
    if (!isActive) {
      setRepositoryRecord(null)
      setDuplicateCandidateId(null)
      setRepositoryRegistrationIdRemovalBlockedKeys(null)
      setIsOwnOrganizationSelected(false)
    }
  }, [isActive])

  const tagOptions = useMemo(() => {
    return (
      tagsData?.data?.tags.map((tag) => ({
        label: tag.name,
        value: tag.name,
      })) ?? []
    )
  }, [tagsData])

  const isOrganizationFieldLocked = useIsOrganizationFieldLockedForBpRepository(
    {
      isUpdate,
      repositoryRecord,
    }
  )

  const onBusinessPartnerRepositorySelected = useCallback(
    (selection: BusinessPartnerRepositoryPrefillSelection) => {
      setDuplicateCandidateId(null)
      setRepositoryRecord(selection.repositoryRecord)
      setIsOwnOrganizationSelected(
        requesterOrganizationId !== undefined &&
          selection.repositoryRecord.id === requesterOrganizationId
      )
      if (selection.lockedRegistrationIds !== undefined) {
        setRepositoryRegistrationIdRemovalBlockedKeys(
          new Set(
            selection.lockedRegistrationIds.map(
              (row) => `${row.type}:${row.registrationId}`
            )
          )
        )
      } else {
        setRepositoryRegistrationIdRemovalBlockedKeys(null)
      }
    },
    [requesterOrganizationId]
  )

  const onBusinessPartnerRepositorySelectionCleared = useCallback(() => {
    setRepositoryRecord(null)
    setDuplicateCandidateId(null)
    setRepositoryRegistrationIdRemovalBlockedKeys(null)
    setIsOwnOrganizationSelected(false)
  }, [])

  const onDuplicateCandidateForCreateNew = useCallback(
    (nextDuplicateCandidateId: string | null) => {
      setDuplicateCandidateId(nextDuplicateCandidateId)
    },
    []
  )

  const existingCanAnswerOnBehalf = supplierMapping.canAnswerOnBehalf ?? false

  const fieldsOptions = useMemo(
    () => ({
      isOrganizationFieldLocked,
      onBusinessPartnerRepositorySelected,
      onBusinessPartnerRepositorySelectionCleared,
      onDuplicateCandidateForCreateNew,
      registrationIdRemovalBlockedKeys:
        repositoryRegistrationIdRemovalBlockedKeys,
      policyFieldOptions: {
        showAnswerOnBehalf: showAnswerOnBehalfPolicy,
        lockAnswerOnBehalfToggle: isUpdate,
        hideAnswerOnBehalfToggle: isOwnOrganizationSelected,
      },
      requesterOrganizationId,
    }),
    [
      isOrganizationFieldLocked,
      onBusinessPartnerRepositorySelected,
      onBusinessPartnerRepositorySelectionCleared,
      onDuplicateCandidateForCreateNew,
      repositoryRegistrationIdRemovalBlockedKeys,
      showAnswerOnBehalfPolicy,
      isUpdate,
      isOwnOrganizationSelected,
      requesterOrganizationId,
    ]
  )

  const fields = useFields(
    isUpdate,
    tagOptions,
    updatePermissions,
    fieldsOptions
  )

  const normalizedInitialValues = useMemo<SupplierFormValues>(() => {
    const canAnswerOnBehalf = showAnswerOnBehalfPolicy
      ? existingCanAnswerOnBehalf
      : undefined

    const baseValues: SupplierFormValues = {
      ...supplierMapping,
      supplierId:
        "supplierId" in supplierMapping
          ? (supplierMapping.supplierId ?? "")
          : "",
      ...(canAnswerOnBehalf !== undefined ? { canAnswerOnBehalf } : {}),
    }

    if (canAnswerOnBehalf === true && !isUpdate) {
      return {
        ...baseValues,
        name: "",
        email: "",
        preferedLangageIso6391: undefined,
      }
    }

    return baseValues
  }, [
    supplierMapping,
    showAnswerOnBehalfPolicy,
    isUpdate,
    existingCanAnswerOnBehalf,
  ])

  const formConfig = useMemo<FormConfig<SupplierFormValues>>(
    () => ({
      validate: zod4Resolver(contactFormSchema),
      initialValues: normalizedInitialValues,
    }),
    [normalizedInitialValues]
  )
  const typesFormKey = useMemo(() => {
    const supplierId =
      "supplierId" in supplierMapping ? (supplierMapping.supplierId ?? "") : ""
    const policySegment =
      normalizedInitialValues.canAnswerOnBehalf === true
        ? "on-behalf"
        : "with-contact"
    return `${isUpdate ? "update" : "create"}-${supplierId}-${
      supplierMapping.email
    }-${policySegment}`
  }, [isUpdate, supplierMapping, normalizedInitialValues.canAnswerOnBehalf])

  return (
    <TypesForm
      key={typesFormKey}
      readonly={updatePermissions.readonly}
      elements={fields}
      cancelLabel={t("supplierSettings.cancel")}
      submitLabel={t("supplierSettings.addSupplier")}
      formConfig={formConfig}
      mutate={(values) => {
        let payload: SupplierMapping = values

        if (!showAnswerOnBehalfPolicy) {
          const { canAnswerOnBehalf: _canAnswerOnBehalf, ...withoutPolicy } =
            values
          payload = withoutPolicy
        } else {
          payload = toPolicyAwarePayload(values, {
            isUpdate,
            existingCanAnswerOnBehalf,
          })
        }

        handleSupplierMapping(payload, {
          repositoryRecord: repositoryRecord ?? undefined,
          duplicateCandidateId: duplicateCandidateId ?? undefined,
        })
      }}
      closeDrawer={onCancel}
    >
      {"supplierId" in supplierMapping &&
        supplierMapping.supplierId &&
        supplierMapping.email && (
          <ProductionSitesInformation supplierEmail={supplierMapping.email} />
        )}
    </TypesForm>
  )
}
